import ReactECharts from 'echarts-for-react';
import ragData from '../data/ragRetrievalBenchmark.json';

/**
 * RetrievalDepthChart — recall / term-hit / MRR by retrieval depth k (Fig 1).
 * Grouped bar; Playano Education orange highlights the metric that actually
 * changes with k (term hit rate), neutral grey for the two that stay flat.
 */

const BRAND = {
  orange: '#FD9800',
  muted: '#9CA3AF',
  mutedDark: '#6B7280',
};

type KItem = { k: number; recall: number; termHit: number; mrr: number; latencyMs: number };

const fmtPct = (v: number) => `${(v * 100).toFixed(0)}%`;

export const RetrievalDepthChart = ({ height = 360 }: { height?: number }) => {
  const fig = (
    ragData as unknown as {
      figures: { retrieval_by_k: { source: string; note: string; items: KItem[] } };
    }
  ).figures.retrieval_by_k;

  const kLabels = fig.items.map((i) => `k=${i.k}`);

  const option = {
    backgroundColor: '#171717',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (params: { marker?: string; seriesName: string; value: number }[]) =>
        params.map((p) => `${p.marker} ${p.seriesName}: <b>${fmtPct(p.value)}</b>`).join('<br/>'),
    },
    legend: { top: 0, textStyle: { color: '#e5e5e5', fontSize: 12 } },
    grid: { left: 52, right: 24, top: 48, bottom: 40 },
    xAxis: {
      type: 'category',
      data: kLabels,
      axisLine: { lineStyle: { color: '#333' } },
      axisLabel: { color: '#e5e5e5', fontSize: 13, fontWeight: 700 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 1,
      name: 'score',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => fmtPct(v) },
      splitLine: { lineStyle: { color: '#333', opacity: 0.5 } },
    },
    series: [
      {
        name: 'Recall@k',
        type: 'bar',
        barGap: '20%',
        data: fig.items.map((i) => i.recall),
        itemStyle: { color: BRAND.muted, borderRadius: [4, 4, 0, 0] },
      },
      {
        name: 'Term hit rate',
        type: 'bar',
        data: fig.items.map((i) => i.termHit),
        itemStyle: { color: BRAND.orange, borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: '#e5e5e5',
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => fmtPct(p.value),
        },
      },
      {
        name: 'MRR',
        type: 'bar',
        data: fig.items.map((i) => i.mrr),
        itemStyle: { color: BRAND.mutedDark, borderRadius: [4, 4, 0, 0] },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 1. Recall, term hit rate, and MRR across retrieval depth k=3/5/10. {fig.note}
      </p>
    </div>
  );
};
